import chroma from 'chroma-js';
import type { ExpressionSpecification } from 'mapbox-gl';
import colors from '~/colors';

// Categorical symbology for ingested shapefile vector layers, colored by the
// `CateTitle` attribute. Pure module (no map/window access) so it is unit-testable
// and reusable by the Step-5 legend. Colors are sourced from `~/colors`.

// Mirrors the API's write-side check; an unrecognised stored value falls back to the derived color.
const HEX_COLOR = /^#[0-9a-f]{6}$/;

// Neutral grey for features whose CateTitle is empty/unknown-empty.
export const UNCATEGORIZED_COLOR = colors.grey50;

// Curated, visually-distinct colors for known CateTitle values. All 23 hexes are
// verified APWA 811-safe (no red/yellow arc, no near-white/near-black, reserved
// arcs only at HSL S ≤ 30%). See docs/superpowers/specs/2026-06-23-apwa-safe-symbology-design.md.
// Exported so the legend reuses it. Do not use colors.* aliases here — literal
// hexes make the safety verification self-contained.
export const CURATED_CATE_TITLE_COLORS: Record<string, string> = {
  // FPL lane types
  through_lane: '#5c7a99', // H=210 S=25% L=48% — steel-blue lane marker
  shoulder: '#6b8c6b', // H=120 S=13% L=48% — sage shoulder
  left_turn_lane: '#b09278', // H= 28 S=26% L=58% — warm buff/tan
  right_turn_lane: '#7a6e9e', // H=255 S=20% L=53% — soft indigo
  slip_turn_lane: '#8c7a5a', // H= 38 S=22% L=45% — warm tan
  edge_of_pavement: '#4a9a9a', // H=180 S=35% L=45% — teal edge line
  back_of_curb: '#555e63', // H=201 S= 8% L=36% — dark slate
  // Ecopia line values
  white_line: '#8ab0b0', // H=180 S=19% L=62% — pale teal (replaces banned white)
  yellow_line: '#8c7850', // H= 40 S=27% L=43% — khaki-brown (replaces banned yellow)
  sidewalk: '#7d8690', // H=212 S= 8% L=53% — cool mid-grey (concrete)
  road_lane_centerline: '#4a8c7a', // H=164 S=31% L=42% — muted teal-green
  road_segment: '#6b5140', // H= 24 S=25% L=34% — warm brown (asphalt)
  road_intersection: '#8c6e80', // H=324 S=12% L=49% — dusty mauve
  crosswalk: '#5f7a9a', // H=213 S=24% L=49% — cool steel-blue (replaces red)
  bike_lane_centerline: '#4a7a5a', // H=140 S=25% L=38% — muted forest-green
  driveway: '#a8b0b0', // H=180 S= 5% L=68% — light warm grey
  planting_strip: '#aab8a0', // H= 95 S=15% L=68% — light sage
  region_line: '#5a5080', // H=253 S=23% L=41% — muted indigo
  // Ecopia polygon (land-cover) values
  water: '#40706c', // H=175 S=27% L=35% — dusty teal-green
  building: '#8c7860', // H= 33 S=19% L=46% — taupe brown
  grass: '#7a9066', // H= 91 S=17% L=48% — sage green
  tree_canopy: '#5a7a55', // H=112 S=18% L=41% — mid-sage
  forest: '#3a5c40', // H=131 S=23% L=29% — muted moss
};

// The colors an uploader may pick from: the curated palette's distinct hexes plus the neutral
// uncategorized grey. The picker also allows any other color, so this is a shortcut rather than a
// constraint.
export const CATEGORY_COLOR_SWATCHES: readonly string[] = [
  ...new Set(Object.values(CURATED_CATE_TITLE_COLORS)),
  UNCATEGORIZED_COLOR,
];

// APWA-safe-by-construction generator:
// (a) HARD-REJECT red arc (HCL H 2–44 → HSL 345–15°) and yellow arc (HCL H 91–112 → HSL 42–66°):
//     rotate the hashed hue to the nearest allowed HCL hue so red/yellow are NEVER emitted.
//     (Empirical HCL→HSL band mapping at C=15; bands differ from C=22 due to perceptual warp.)
// (b) C (chroma in HCL) capped at 15 — guarantees HSL S ≤ 30% for all reserved arcs across
//     all three lightness levels. C=22 breaches the 30% floor at L=68 in the blue arc.
// (c) Lightness bounded to mid-range {40, 55, 68} — no near-black or near-white output.
const HCL_CHROMA_CAP = 15;
const HCL_LIGHTNESS_LEVELS = [40, 55, 68] as const;

// Remap HCL hues that produce banned HSL arcs (empirically derived at C=15) to nearest safe hue.
// Red HSL arc (345–15°): HCL 2–44 → redirect to HCL 45.
// Yellow HSL arc (42–66°): HCL 91–112 → redirect to HCL 90 (below) or 113 (above).
function apwaSafeHclHue(h: number): number {
  if (h >= 2 && h <= 44) return 45;
  if (h >= 91 && h <= 112) return h <= 101 ? 90 : 113;
  return h;
}

function generatedColorForKey(key: string): string {
  const rawHue = stableHash(key) % 360;
  const hue = apwaSafeHclHue(rawHue);
  const lightness = HCL_LIGHTNESS_LEVELS[stableHash(`${key}#l`) % HCL_LIGHTNESS_LEVELS.length];
  return chroma.hcl(hue, HCL_CHROMA_CAP, lightness).hex();
}

// Title-cases a snake_case / whitespace CateTitle value. Empty => "Uncategorized".
export function humanizeCateTitle(value: string): string {
  const words = value
    .split(/[\s_]+/)
    .map((w) => w.trim())
    .filter(Boolean);
  if (words.length === 0) return 'Uncategorized';
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

// A category's display name: the uploader's chosen label when it is not blank, else the name
// humanized from the raw CateTitle value.
export function categoryDisplayName(category: { value: string; label?: string }): string {
  const label = category.label?.trim();
  return label ? label : humanizeCateTitle(category.value);
}

// Deterministic 32-bit string hash (FNV-1a-ish) so unknown values map stably to a
// fallback palette index. Same input => same output across calls/sessions.
function stableHash(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

// Normalizes a CateTitle to its curated-lookup / hash key: trim, lowercase, collapse
// whitespace to underscores — so casing/spacing variants of a known value
// ('Through_Lane', 'THROUGH LANE') still resolve to the curated color.
function normalizeCateTitleKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

export function colorForCateTitle(value: string): string {
  if (value.trim() === '') return UNCATEGORIZED_COLOR;
  const key = normalizeCateTitleKey(value);
  if (Object.hasOwn(CURATED_CATE_TITLE_COLORS, key)) return CURATED_CATE_TITLE_COLORS[key];
  return generatedColorForKey(key);
}

// A category's map color: the uploader's pick when it is a valid hex, else the palette derived from
// the value. Validating on read keeps a stored value that is not a valid hex from producing an
// invalid paint value, which would tear down the layer stack for every viewer of the project.
export function colorForCategory(category: { value: string; color?: string }): string {
  if (category.color && HEX_COLOR.test(category.color)) return category.color;
  return colorForCateTitle(category.value);
}

// Own-property lookup: a category named `constructor` or `toString` would otherwise resolve to a
// function inherited from Object.prototype, which a `??` fallback lets through as a color.
export function colorWithOverride(
  category: { value: string; color?: string },
  overrides: Record<string, string>,
): string {
  return Object.hasOwn(overrides, category.value) ? overrides[category.value] : colorForCategory(category);
}

// Builds a Mapbox GL `match` expression coloring features by CateTitle. A `match`
// with zero label/output pairs is invalid, so for empty categories we return a plain
// fallback color string (still a valid `line-color` paint value).
export function lineColorMatchExpression(
  categories: readonly { value: string; color?: string }[],
): ExpressionSpecification | string {
  // Dedupe: Mapbox `match` throws on duplicate labels, which would tear down the whole
  // layer stack. The server sends distinct values, but guard against a future regression.
  const seen = new Set<string>();
  const branches: string[] = [];
  for (const category of categories) {
    if (seen.has(category.value)) continue;
    seen.add(category.value);
    branches.push(category.value, colorForCategory(category));
  }
  // A `match` with zero label/output pairs is invalid — fall back to a plain color string.
  if (branches.length === 0) return UNCATEGORIZED_COLOR;
  // Coalesce first: a feature with no CateTitle yields null, which matches no branch — including
  // the '' branch the uploader may have picked a color for.
  // Final element is the fallback output for any unmatched CateTitle.
  return ['match', ['coalesce', ['get', 'CateTitle'], ''], ...branches, UNCATEGORIZED_COLOR] as ExpressionSpecification;
}
